import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/lib/api";
import { useDashboard } from "./api";
import { DelinquentBanner } from "./DelinquentBanner";

// Phase 4a customer-portal — top-level shell. Includes the global
// header (FR-18 — logo + Portal label + avatar/menu mobile-collapsed
// to logo + avatar) and the delinquency banner (AC-9, stub-driven in
// v1). Children render via <Outlet />.
//
// Auth gate (FR-1 / AC-1) — subscribes to the React-Query cache for
// **any** portal query failing with 401, not just the dashboard. The
// dashboard query is still rendered for the delinquency banner, but
// the redirect itself is cache-driven so deep links like
// `/portal/orders/X` bounce too when the session expires there.
export function PortalLayout(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dashboard = useDashboard();

  useEffect(() => {
    const cache = queryClient.getQueryCache();
    const unsubscribe = cache.subscribe((event) => {
      // Only react to the specific query that just transitioned —
      // scanning the whole cache would also match stale 401s left
      // over from a previous (now signed-back-in) session, causing
      // an unwanted bounce on the next cache event.
      if (event?.type !== "updated") return;
      const query = event.query;
      const key = query.queryKey;
      if (!Array.isArray(key) || key[0] !== "portal") return;
      const err = query.state.error;
      if (!(err instanceof ApiError) || err.status !== 401) return;
      // Only when the query is idle (fetch settled) — react-query
      // emits "updated" with the previous error still set while a
      // refetch is in flight; ignoring those avoids double-bounce.
      if (query.state.fetchStatus !== "idle") return;

      // Clear stale portal queries so a return visit fetches fresh
      // and doesn't trip this handler again with the cached 401.
      queryClient.removeQueries({ queryKey: ["portal"] });
      const back = encodeURIComponent(location.pathname + location.search);
      navigate(`/sign-in?return_to=${back}`, { replace: true });
    });
    return unsubscribe;
  }, [queryClient, location.pathname, location.search, navigate]);

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-4 md:px-6 md:py-6">
      <PortalHeader />
      {dashboard.data?.delinquent ? <DelinquentBanner /> : null}
      <Outlet />
    </div>
  );
}

function PortalHeader(): JSX.Element {
  return (
    <header className="flex items-center justify-between border-b border-zinc-200 pb-3">
      <Link to="/" className="text-base font-semibold text-brand-black">
        JDR Equipment Rentals
      </Link>
      <span className="hidden text-sm text-zinc-500 md:inline">Portal</span>
      <Link to="/portal/profile" className="text-sm text-zinc-700 underline">
        My account
      </Link>
    </header>
  );
}
